/**
 * LinguistPro Configuration Constants — v5.6.1
 * Centralised configuration for audio processing, API behaviour, and app metadata
 */

export const AUDIO_CONFIG = {
  /** Duration in seconds for each transcription window (legacy, kept for audio decode) */
  WINDOW_DURATION: 30,
  /** Overlap between windows for continuity (seconds) */
  OVERLAP_DURATION: 5,
  /** Initial sample duration for speaker identification (seconds) */
  PHASE1_SAMPLE_DURATION: 30,
  /** Delay between API calls to avoid rate limits (milliseconds) */
  INTER_SEGMENT_COOLING: 5000,
  /** Audio sample rate for processing (Hz) */
  SAMPLE_RATE: 16000,
  /** Frame extraction timestamps for video OCR (seconds) */
  FRAME_TIMESTAMPS: [5, 30, 50, 70, 90],
  /** JPEG quality for frame extraction (0-1) — high quality preserves text legibility for Gemini OCR */
  FRAME_QUALITY: 0.92,
} as const;

export const API_CONFIG = {
  /** Maximum retry attempts for failed API calls */
  MAX_RETRIES: 3,
  /** Initial delay before retry (milliseconds) */
  INITIAL_RETRY_DELAY: 3000,
  /** Delay multiplier for exponential backoff */
  RETRY_MULTIPLIER: 1.5,
  /** Extended cooling period for quota errors (milliseconds) */
  QUOTA_COOLING: 15000,
  /** Primary engine for large files (>4MB): Afrikaans + English code-switching, diarisation, 5GB/10hr */
  PRIMARY_ENGINE_LARGE: 'assemblyai' as const,
  /** Primary engine for small files (<4MB): multilingual, code-switching, diarisation */
  PRIMARY_ENGINE_SMALL: 'gemini' as const,
  /** Fallback engine for small files when Gemini circuit breaker opens */
  FALLBACK_ENGINE: 'deepgram' as const,
  /** AssemblyAI model routing: Universal-3.5-Pro (English) + Universal-2 (Afrikaans + 98 other languages) */
  ASSEMBLYAI_MODELS: ['universal-3-5-pro', 'universal-2'] as const,
  /** Deepgram model (fallback for small English-only files) */
  DEEPGRAM_MODEL: 'nova-3',
  /** Groq model */
  GROQ_MODEL: 'whisper-large-v3-turbo',
  /**
   * Engine identifier recorded on transcriptions from the browser-side chunked
   * path. MIRROR ONLY of GEMINI_TRANSCRIBE_ENGINE_ID in `lib/models.ts`, same
   * reason and same test guard.
   */
  TRANSCRIBE_ENGINE_ID: 'gemini-3.8-flash',
} as const;

export const APP_VERSION = 'v5.6.1';

export const SYSTEM_STATUS = {
  NOMINAL: 'nominal',
  PROCESSING: 'processing',
  RETRYING: 'retrying',
  ERROR: 'error',
} as const;

export type SystemStatusType = typeof SYSTEM_STATUS[keyof typeof SYSTEM_STATUS];
